import { Controller } from "@hotwired/stimulus"
import * as Log from "web-ui/log"

const refreshIntervalMilliseconds = 10 * 1000

// Connects to data-controller="dynamic-form-refresh"
export default class extends Controller {
  static values = {
    path: String
  }

  connect() {
    this.debug("Connect", { path: this.pathValue })

    this.connected = true

    this.refreshing = false
    this.refreshIntervalStartTime = Date.now()

    this.startRefreshInterval()

    document.addEventListener("visibilitychange", this.handleVisibilityChange)

    // Any form submission must abort the refresh so that the result of the
    // refresh cannot override the result of the submission
    // - Aaron, Shaun, Fri Mar 21 2025
    document.addEventListener("submit", this.abortRefresh, { capture: true })
    this.element.addEventListener("dynamic-form:before-update", this.abortRefresh)
  }

  disconnect() {
    this.debug("Disconnect")

    this.connected = false

    document.removeEventListener("visibilitychange", this.handleVisibilityChange)
    document.removeEventListener("submit", this.abortRefresh)
    this.element.removeEventListener("dynamic-form:before-update", this.abortRefresh)

    this.stopRefreshInterval()
    this.abortRefresh()
  }

  startRefreshInterval() {
    this.stopRefreshInterval()

    this.refreshInterval = setInterval(() => {
      if (this.shouldRefresh()) {
        this.refresh()
      }
    }, refreshIntervalMilliseconds / 10)
  }

  stopRefreshInterval() {
    clearInterval(this.refreshInterval)
  }

  handleVisibilityChange = () => {
    if (this.shouldRefresh()) {
      this.refresh()
    }
  }

  // This may change the refreshIntervalStartTime because a refresh should not
  // occur immediately after an update in order to ensure that the validate
  // param is properly set. If the update sets the validate param, but has not
  // yet fully rendered, the refresh could happen without the validate param
  // set, causing it to regress.
  // - Aaron, Fri Mar 21 2025
  shouldRefresh() {
    if (document.hidden) {
      return false
    }

    const dynamicFormController = this.application.getControllerForElementAndIdentifier(this.element, "dynamic-form")

    if (dynamicFormController.hasPendingUpdates() || dynamicFormController.isUpdating()) {
      this.refreshIntervalStartTime = Date.now()
      return false
    }

    const busyElement = document.querySelector("[busy],[aria-busy]")

    // Do not refresh while a turbo submission is taking place - Aaron, Shaun, Fri Mar 21 2025
    if (busyElement != null) {
      this.refreshIntervalStartTime = Date.now()
      return false
    }

    const now = Date.now()
    const millisecondsSinceStartTime = now - this.refreshIntervalStartTime

    if (millisecondsSinceStartTime < refreshIntervalMilliseconds) {
      return false
    }

    return true
  }

  async refresh() {
    if (this.refreshing) {
      return
    }

    this.debug("Starting refresh")

    this.refreshAbortController = new AbortController()
    const signal = this.refreshAbortController.signal
    this.refreshing = true
    this.refreshIntervalStartTime = Date.now()

    const path = this.pathValue
    const headers = {
      "Accept": "text/vnd.turbo-stream.html"
    }

    try {
      const response = await fetch(path, { headers, signal })

      if (!response.ok) {
        return
      }

      const html = await response.text()

      if (!this.connected) {
        return
      }

      this.debug("Rendering")

      Turbo.renderStreamMessage(html)

      this.debug("Refreshed")

    } catch(error) {
      if (!this.connected) {
        return
      }

      Sentry.captureException(error)
    }

    this.refreshing = false
    this.refreshAbortController = null
  }

  abortRefresh = () => {
    if (this.refreshAbortController) {
      this.debug("Aborting refresh")
      this.refreshAbortController.abort()
    }
  }

  debug(message, data) {
    Log.debug("Dynamic Form Refresh - " + message, data)
  }
}
